import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Typography, Tag  ,Tooltip, Drawer,} from "antd";
import { ArrowLeftOutlined, FileOutlined, PlusOutlined,   QuestionCircleOutlined} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchReceipts, deleteReceipt } from "./customerReceiptThunk";
import {
  resetReceipts,
  setSelectedReceipt,
  setIsEditReceipt,
} from "./customerReceiptSlice";

import commaNumber from "comma-number";
import { formatDateOnly, getAccessType } from "../../lib/functions";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { App } from "antd";
import { useIsAdminUser } from "../../lib/accessList";
import { useTxLock } from "../../hooks/useTxLock";
import { Grid } from "antd";

const { useBreakpoint } = Grid;

const format = commaNumber.bindWith(",", ".");

const CustomerReceiptList = () => {
  const { Title } = Typography;
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { receipts, loading } = useSelector((state) => state.customerReceipts);
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const isAdminUser = useIsAdminUser();
  const screens = useBreakpoint();
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  useEffect(() => {
    const access = getAccessType("debtor_receipt");
    access_type_set(access);
  }, []);
  useEffect(() => {
    dispatch(resetReceipts()); // clear previous state if needed
    dispatch(fetchReceipts());
  }, [dispatch]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };


  const handleDelete = async (receipt_id, receipt_name) => {
    try {
      await dispatch(deleteReceipt(receipt_id)).unwrap();
      modal.success({
        title: "Receipt entry deleted successfully",
      });
      dispatch(fetchReceipts());
    } catch (error) {
      console.log(error, "error123");
      modal.warning({
        title: error || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Receipt Code",
      dataIndex: "rcv_code",
    },
    {
      title: "Receipt Date",
      dataIndex: "created_date",
      render: (value) => (value ? formatDateOnly(value) : "-"),
    },
    {
      title: "Customer",
      render: (_, record) =>
        record.details && record.details.length > 0
          ? record.details[0].from_acc_name
          : "",
    },
    {
      title: "Total Discount",
      dataIndex: "discount_total",
      render: (val) => Number(val || 0).toFixed(2),
    },
    {
      title: "Received Total",
      dataIndex: "rcv_total",
      render: (val) => Number(val || 0).toFixed(2),
    },
    {
      title: "Description",
      dataIndex: "narration",
    },
    {
      title: "Created By",
      dataIndex: "full_name",
    },
  {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <>
        <Button
          type="link"
          icon={<FileOutlined />}
          onClick={() =>
            navigate(`/accounts/debitor-receipts/view/${record.rcv_id}`)
          }
        />

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            icon={<EditOutlined />}
            disabled={!(isAdminUser || canEdit) || txLocked}
            onClick={() => {
              if (txLocked) return;

              dispatch(setSelectedReceipt(record));
              dispatch(setIsEditReceipt(true));

              navigate(
                `/accounts/debitor-receipts/edit/${record.rcv_id}`
              );
            }}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            disabled={!(isAdminUser || canDelete) || txLocked}
            onClick={() => {
              if (txLocked) return;

              modal.confirm({
                title: "Are you sure?",
                content: `This will delete customer receipt with amount: ${record.rcv_total}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () => handleDelete(record.rcv_id),
              });
            }}
          />
        </Tooltip>
      </>
    );
  },
}
  ];

  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "RECEIVED INTO", dataIndex: "into_acc_name" },
    {
      title: "Amount",
      dataIndex: "rcv_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },

    {
      title: "Status",
      dataIndex: "payment_status",
      align: "center",
      render: (val) => {
        const color =
          val === "approved" ? "green" : val === "rejected" ? "red" : "gold";
        return (
          <Tag color={color} data-testid={`receipt-status-${val}`}>
            {val.charAt(0).toUpperCase() + val.slice(1)}
          </Tag>
        );
      },
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="customer-receipts-back-button"
        />
        <Title
          level={3}
          style={{
            margin: 0,
            wordBreak: "break-word",
            fontSize: screens.md ? undefined : "18px",
          }}
          data-testid="page-title"
        >
          Customer / Supplier Receipts
        </Title>

        <div style={{ marginLeft: "auto" }}>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      </div>
      <Row justify="end" style={{ marginBottom: 32, marginTop: 32 }}>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/accounts/debitor-receipts/add")}
                data-testid="btn-add-receipt"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={receipts}
        rowKey={(record, index) => `${record.rcv_id}-${index}`}
        data-testid="customer-receipt-table"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        size="middle"
        scroll={{ x: "max-content" }}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <Table
                rowKey="detailId"
                columns={detailColumns}
                dataSource={record.details || []}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: (record.details || []).length,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onChange: (page) => {
                    setCurrentPage(page);
                  },
                  onShowSizeChange: (_, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  },
                }}
                size="small"
                bordered
                data-testid="receipt-detail-table"
              />
            );
          },
        }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
      <Drawer
        title="Customer Receipt Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Customer / Supplier Receipts
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Customer / Supplier Receipt module is used to record and manage
            payment receipts received from customers or payments collected for suppliers
            within the ERP accounting system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View receipt transaction records</li>
              <li>Use <strong>Add New</strong> to create receipt entries</li>
              <li>View detailed receipt information</li>
              <li>Edit receipt details when required</li>
              <li>Delete receipts if permitted</li>
              <li>Track customer and supplier payment receipts</li>
              <li>Monitor received totals and discounts</li>
              <li>Review payment approval statuses</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Receipt entries affect accounting balances immediately.</li>
              <li>Payment status indicates approval or rejection state.</li>
              <li>Receipt details are available through expandable rows.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Transaction history is maintained for auditing and reporting.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default CustomerReceiptList;
